import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { useRouter } from 'expo-router';

import { useIsFocused } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  GroupOrderSignedOutModal,
  LoadingAnimation,
  LocationSearch,
  NotFoundView,
  TransparentModal,
} from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useNavigateBack } from '../../navigation';
import { GroupOrderRouteParams } from './GroupOrderSetupScreen.types';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderSearchLocationScreen = () => {
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const isFocused = useIsFocused();

  const navigateBack = useNavigateBack();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  // ─────────────────────────────────────────────────────────────────────

  if (!isGroupOrderingEnabled) {
    return <NotFoundView />;
  }

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  if (!isLoggedIn) {
    return <GroupOrderSignedOutModal onClose={handleDismiss} />;
  }

  if (!isFocused) return null;

  return <GroupOrderSearchLocationScreenContent />;
};

export const GroupOrderSearchLocationScreenContent = () => {
  const router = useRouter();

  const navigateBack = useNavigateBack();

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    navigateBack();
  }, [navigateBack]);

  const navigateToSetup = useCallback(
    (params: GroupOrderRouteParams) => {
      router.dismissTo({ pathname: '/group-ordering/create', params });
    },
    [router],
  );

  const navigateToLocation = useCallback<OnOpenRestaurant>(
    (context) => {
      const restaurantId = context.activeLocation?.restaurantId;

      if (!restaurantId) return;

      navigateToSetup({ restaurantId });
    },
    [navigateToSetup],
  );

  const navigateToDeliveryLocation = useCallback<OnOpenDeliveryRestaurant>(
    (context) => {
      const { deliveryLocation } = context;

      if (!deliveryLocation?.restaurantId) return;

      navigateToSetup({
        restaurantId: deliveryLocation.restaurantId,
        deliveryAddressId: deliveryLocation.addressId,
      });
    },
    [navigateToSetup],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size="large" offset="10%" onClose={handleDismiss} transparent>
      <LocationSearch
        navigateToLocation={navigateToLocation}
        navigateToDeliveryLocation={navigateToDeliveryLocation}
        interactedLocationRestaurantSlug={undefined}
        interactedLocationDeliveryAddressId={undefined}
        isInsideModal
      />
    </TransparentModal>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchProps = ComponentProps<typeof LocationSearch>;

type OnOpenRestaurant = NonNullable<LocationSearchProps['navigateToLocation']>;
type OnOpenDeliveryRestaurant = NonNullable<LocationSearchProps['navigateToDeliveryLocation']>;
